// src/components/Admin/LocationManager.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; // Import db from your firebase.js
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';

function LocationManager() {
  const [newLocationName, setNewLocationName] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const locationsCollectionRef = collection(db, 'locations');

  // Function to fetch locations
  const fetchLocations = async () => {
    try {
      setLoading(true); // Set loading to true when fetching starts
      const q = query(locationsCollectionRef, orderBy('name')); // Order alphabetically
      const data = await getDocs(q);
      setLocations(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching locations:", err);
      setError("Failed to load locations. Please try again.");
      setLoading(false);
    }
  };

  // Fetch locations from Firestore on component mount
  useEffect(() => {
    fetchLocations();
  }, []); // Empty dependency array means this runs once on mount

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) {
      alert("Location name cannot be empty.");
      return;
    }
    try {
      // Check if location already exists (case-insensitive)
      const existingLocation = locations.find(loc => loc.name.toLowerCase() === newLocationName.trim().toLowerCase());
      if (existingLocation) {
        alert(`Location '${newLocationName.trim()}' already exists.`);
        setNewLocationName('');
        return;
      }

      await addDoc(locationsCollectionRef, { name: newLocationName.trim() });
      setNewLocationName('');
      await fetchLocations(); // Re-fetch locations to update the list
    } catch (err) {
      console.error("Error adding location:", err);
      // More descriptive error if possible from Firebase (e.g., auth/permission-denied)
      setError("Failed to add location. Please ensure you are logged in as an admin and have permissions.");
    }
  };

  const handleDeleteLocation = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete '${name}'? This cannot be undone.`)) {
      try {
        const locationDoc = doc(db, 'locations', id);
        await deleteDoc(locationDoc);
        setLocations(locations.filter(loc => loc.id !== id)); // Remove from local state
      } catch (err) {
        console.error("Error deleting location:", err);
        setError("Failed to delete location. Please ensure you are logged in as an admin and have permissions.");
      }
    }
  };

  // --- Rendering part ---
  if (loading) return <p style={messageStyle}>Loading locations...</p>;
  if (error) return <p style={errorStyle}>Error: {error}</p>;

  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>Manage Locations</h2>
      <form onSubmit={handleAddLocation} style={formStyle}>
        <input
          type="text"
          placeholder="New location name"
          value={newLocationName}
          onChange={(e) => setNewLocationName(e.target.value)}
          style={inputStyle}
        />
        <button type="submit" style={buttonStyle}>Add Location</button>
      </form>

      <div style={listContainerStyle}>
        {locations.length === 0 ? (
          <p style={messageStyle}>No locations added yet.</p>
        ) : (
          <ul style={listStyle}>
            {locations.map((location) => (
              <li key={location.id} style={listItemStyle}>
                <span>{location.name}</span> {/* Display the location name */}
                <button onClick={() => handleDeleteLocation(location.id, location.name)} style={deleteButtonStyle}>Delete</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Basic Inline Styles (from previous response, re-included for completeness)
const containerStyle = {
  maxWidth: '600px',
  margin: '20px auto',
  padding: '20px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  fontFamily: 'Arial, sans-serif',
  backgroundColor: '#f9f9f9',
};

const headingStyle = {
  textAlign: 'center',
  color: '#333',
  marginBottom: '20px',
};

const formStyle = {
  display: 'flex',
  gap: '10px',
  marginBottom: '20px',
};

const inputStyle = {
  flexGrow: '1',
  padding: '10px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '16px',
};

const buttonStyle = {
  padding: '10px 15px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '16px',
};

const listContainerStyle = {
  maxHeight: '400px', // Example: make list scrollable if many items
  overflowY: 'auto',
  borderTop: '1px solid #eee',
  paddingTop: '15px',
};

const listStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: '1px dashed #eee',
  fontSize: '16px',
  color: '#555',
};

const deleteButtonStyle = {
  backgroundColor: '#f44336',
  color: 'white',
  border: 'none',
  padding: '5px 10px',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '14px',
};

const messageStyle = {
  textAlign: 'center',
  padding: '10px',
  color: '#666',
};

const errorStyle = {
  textAlign: 'center',
  padding: '10px',
  color: 'red',
  fontWeight: 'bold',
};

export default LocationManager;